import React, { FC, ReactElement, useContext, useEffect, useMemo, useState } from 'react'
import { PartitionModal } from '../../../../i18n'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { FormProvider, useForm, UseFormMethods } from '../../../Vendor/react-hook-form/dist/index.ie11'
import { MessageContext, ReactState } from '../../Common/Contexts'
import { MessageContextType } from '../../Common/Contexts/MessageContext'
import {
    createPartition,
    getPartition,
    getTelSysInternal,
    updatePartition
} from '../../../NonReact/common/API/EntityAPIClient'
import { isStringNotEmpty, IsVarEmpty, isVarNotEmpty } from '../../../NonReact/utils/GlobalFunctions'
import { EMPTY_STRING } from '../../Common/Constants/Separators'
import Tabs from '../../Common/Components/Tabs'
import { IconName } from '../../Common/Components/Icon'
import { Tab } from '../../Common/Components/Tabs/Types'
import { ON_CHANGE } from '../../Common/Constants/HookFormEventNames'
import { PartitionConfigurationTabID } from '../Constants/Constants'
import { PartitionConfigurationForm, PartitionConfigurationRequest } from '../Types/Types'
import { mapPartitionDtoToForm, mapPartitionFormToRequest } from '../Utils/mappers'
import { getPartitionModalTelSysLayout, PartitionModalTelSysLayout } from '../Utils/partitionModalTelSysLayout'
import { PartitionGeneralTab } from '../Tabs/PartitionGeneralTab'
import { PartitionSiteSettingsTab } from '../Tabs/PartitionSiteSettingsTab'
import { PartitionSyncTab } from '../Tabs/PartitionSyncTab'
import { PartitionAdvancedTab } from '../Tabs/PartitionAdvancedTab'
import { PartitionCommunicationStatusTab } from '../Tabs/PartitionCommunicationStatusTab'
import { RoundedButton } from '../../../ReactRouting/Components/Atoms/RoundedButton/RoundedButton'
import { JSONResponse } from '../../../NonReact/common/Types/Types'
import { PartitionAdminDTO, TelSysAdminDTO } from '../../../NonReact/Types/Types'
import { TelSysTypes } from '../../../NonReact/constants/TelephonyServerTypes'
import { PARTITION_IS_GLOBAL_FIELD_NAME } from '../Constants/FieldNames'
import { usePromiseValue, UsePromiseValueResult } from '../../Common/Hooks/usePromiseValue'

/**
 * Resolves a TelSys admin DTO type string to {@link TelSysTypes} when it matches a known enum value.
 *
 * @param value - Raw `TelSysType` from the internal TelSys-by-ID payload ({@link TelSysAdminDTO}).
 * @returns Matching enum value, or `null` when empty or unknown.
 */
const telSysTypeFromAdminDto = (value: Definable<string>): Nullable<TelSysTypes> => {
    if (IsVarEmpty(value)) {
        return null
    }

    const knownTypes: readonly string[] = Object.values(TelSysTypes) as string[]
    if (!knownTypes.includes(value)) {
        return null
    }

    return value as TelSysTypes
}

type Props = {
    closeModal: () => void
    refreshAction: () => void
    filterTelSysID: number
    selectedPartitionIDInTelSys?: number
    viewOnly?: boolean
}

/**
 * Partition modal tabs and submit workflow.
 *
 * @param props - Tabs props.
 * @returns Tabs content with form state and actions.
 */
export const PartitionTabs: FC<Props> = ({
    closeModal,
    refreshAction,
    filterTelSysID,
    selectedPartitionIDInTelSys,
    viewOnly
}: Props): ReactElement => {
    const language: CLanguageHandler = CLanguageHandler.getInstance()
    const { displayError }: MessageContextType = useContext(MessageContext)
    const isEditMode: boolean = !IsVarEmpty(selectedPartitionIDInTelSys)
    const [activeTabID, setActiveTabID]: ReactState<number> = useState<number>(PartitionConfigurationTabID.GENERAL)
    const [serverError, setServerError]: ReactState<string> = useState<string>(EMPTY_STRING)
    const [isSubmitting, setIsSubmitting]: ReactState<boolean> = useState<boolean>(false)
    const [originalLicensedForRptUserType, setOriginalLicensedForRptUserType]: ReactState<string> =
        useState<string>(EMPTY_STRING)
    const [partitionCommStatusReadOnly, setPartitionCommStatusReadOnly]: ReactState<{
        telSysConfigSyncStartedAt: Nullable<string>
        telSysConfigSyncLastError: Nullable<string>
    }> = useState<{
        telSysConfigSyncStartedAt: Nullable<string>
        telSysConfigSyncLastError: Nullable<string>
    }>({
        telSysConfigSyncStartedAt: null,
        telSysConfigSyncLastError: null
    })

    const {
        value: parentTelSys,
        error: parentTelSysError
    }: UsePromiseValueResult<Nullable<JSONResponse<TelSysAdminDTO>>> = usePromiseValue<
        Nullable<JSONResponse<TelSysAdminDTO>>
    >(
        (): Promise<JSONResponse<TelSysAdminDTO>> => getTelSysInternal(filterTelSysID),
        {
            initialValue: null,
            enabled: true
        },
        [filterTelSysID]
    )
    const {
        value: selectedPartition,
        loading: selectedPartitionLoading,
        error: selectedPartitionError
    }: UsePromiseValueResult<Nullable<PartitionAdminDTO>> = usePromiseValue<Nullable<PartitionAdminDTO>>(
        (): Promise<Nullable<PartitionAdminDTO>> => getPartition(filterTelSysID, selectedPartitionIDInTelSys as number),
        {
            initialValue: null,
            enabled: isEditMode && !IsVarEmpty(selectedPartitionIDInTelSys)
        },
        [filterTelSysID, selectedPartitionIDInTelSys, isEditMode]
    )
    const isAwaitingFirstSelectedPartitionResult: boolean =
        isEditMode && !selectedPartitionLoading && IsVarEmpty(selectedPartitionError) && IsVarEmpty(selectedPartition)
    const parentTelSysType: Nullable<TelSysTypes> = useMemo(
        (): Nullable<TelSysTypes> => telSysTypeFromAdminDto(parentTelSys?.TelSysType),
        [parentTelSys?.TelSysType]
    )
    const isLoading: boolean = isEditMode && (selectedPartitionLoading || isAwaitingFirstSelectedPartitionResult)
    const telSysLayout: PartitionModalTelSysLayout = getPartitionModalTelSysLayout(parentTelSysType)

    const methods: UseFormMethods<PartitionConfigurationForm> = useForm<PartitionConfigurationForm>({
        defaultValues: mapPartitionDtoToForm(null),
        mode: ON_CHANGE,
        reValidateMode: ON_CHANGE,
        shouldUnregister: false
    })
    const { handleSubmit, reset, watch, errors }: UseFormMethods<PartitionConfigurationForm> = methods
    const isGlobalPartition: boolean = !!watch(PARTITION_IS_GLOBAL_FIELD_NAME, false)
    const validationErrors: boolean[] = useMemo((): boolean[] => {
        const hasSynchronisationTabGeneralErrors: boolean =
            !!errors.general?.telSysComponentType || !!errors.general?.idOnTelSysOwner || !!errors.general?.idOnTelSys

        return [
            !!errors.general,
            !!errors.siteSettings,
            !!errors.synchronisation || hasSynchronisationTabGeneralErrors,
            !!errors.advanced,
            false
        ]
    }, [JSON.stringify(errors)])

    useEffect((): void => {
        if (isEditMode && isVarNotEmpty(selectedPartition)) {
            setOriginalLicensedForRptUserType(selectedPartition?.LicensedForRptUserType ?? EMPTY_STRING)
            reset(mapPartitionDtoToForm(selectedPartition, parentTelSys))
            setPartitionCommStatusReadOnly({
                telSysConfigSyncStartedAt: selectedPartition?.TelSysConfigSyncStartedAt ?? null,
                telSysConfigSyncLastError: selectedPartition?.TelSysConfigSyncLastError ?? null
            })
        }
    }, [isEditMode, parentTelSys, reset, selectedPartition])

    useEffect((): void => {
        if (isEditMode) return

        setOriginalLicensedForRptUserType(parentTelSys?.LicensedForRptUserType ?? EMPTY_STRING)
    }, [isEditMode, parentTelSys?.LicensedForRptUserType])

    useEffect((): void => {
        if (isEditMode) return

        reset(mapPartitionDtoToForm(null, parentTelSys))
    }, [isEditMode, parentTelSys, reset])

    useEffect((): void => {
        if (IsVarEmpty(parentTelSysError)) return

        displayError(parentTelSysError)
    }, [displayError, parentTelSysError])

    useEffect((): void => {
        if (IsVarEmpty(selectedPartitionError)) return

        displayError(selectedPartitionError)
    }, [displayError, selectedPartitionError])

    const submitForm = async (form: PartitionConfigurationForm): Promise<void> => {
        setIsSubmitting(true)
        setServerError(EMPTY_STRING)
        const request: PartitionConfigurationRequest = mapPartitionFormToRequest(form, filterTelSysID, telSysLayout)

        const onSuccess = (): void => {
            closeModal()
            refreshAction()
        }
        const onError = (error: Error): void => {
            setServerError(error.message ?? language.get(PartitionModal.PartitionModal_RequestFailed))
            setIsSubmitting(false)
        }

        if (isEditMode && !IsVarEmpty(selectedPartitionIDInTelSys)) {
            updatePartition(filterTelSysID, selectedPartitionIDInTelSys, request).then(onSuccess).catch(onError)
            return
        }

        createPartition(filterTelSysID, request).then(onSuccess).catch(onError)
    }

    const tabs: Tab[] = [
        {
            ID: PartitionConfigurationTabID.GENERAL,
            isDisplayed: true,
            tabsMenuItem: {
                title: language.get(PartitionModal.PartitionModal_Tab_Details),
                iconName: IconName.SETTINGS,
                onClick: (): void => setActiveTabID(PartitionConfigurationTabID.GENERAL)
            },
            content: (
                <PartitionGeneralTab
                    isEditMode={isEditMode}
                    originalLicensedForRptUserType={originalLicensedForRptUserType}
                    viewOnly={viewOnly}
                    telSysLayout={telSysLayout}
                />
            ),
            hasError: validationErrors[PartitionConfigurationTabID.GENERAL]
        },
        {
            ID: PartitionConfigurationTabID.SITE_SETTINGS,
            isDisplayed: true,
            tabsMenuItem: {
                title: language.get(PartitionModal.PartitionModal_Tab_SiteSettings),
                iconName: IconName.PIN_NUM_OUTLINED,
                onClick: (): void => setActiveTabID(PartitionConfigurationTabID.SITE_SETTINGS)
            },
            content: (
                <PartitionSiteSettingsTab viewOnly={viewOnly} telSysLayout={telSysLayout} telSysID={filterTelSysID} />
            ),
            hasError: validationErrors[PartitionConfigurationTabID.SITE_SETTINGS]
        },
        {
            ID: PartitionConfigurationTabID.SYNCHRONISATION,
            isDisplayed: !isGlobalPartition,
            tabsMenuItem: {
                title: language.get(PartitionModal.PartitionModal_Tab_Synchronisation),
                iconName: IconName.SYNC,
                onClick: (): void => setActiveTabID(PartitionConfigurationTabID.SYNCHRONISATION)
            },
            content: <PartitionSyncTab viewOnly={viewOnly} telSysLayout={telSysLayout} />,
            hasError: validationErrors[PartitionConfigurationTabID.SYNCHRONISATION]
        },
        {
            ID: PartitionConfigurationTabID.ADVANCED,
            isDisplayed: true,
            tabsMenuItem: {
                title: language.get(PartitionModal.PartitionModal_Tab_Advanced),
                iconName: IconName.WRENCH,
                onClick: (): void => setActiveTabID(PartitionConfigurationTabID.ADVANCED)
            },
            content: <PartitionAdvancedTab viewOnly={viewOnly} telSysLayout={telSysLayout} />,
            hasError: validationErrors[PartitionConfigurationTabID.ADVANCED]
        },
        {
            ID: PartitionConfigurationTabID.COMMUNICATION_STATUS,
            isDisplayed: isEditMode,
            tabsMenuItem: {
                title: language.get(PartitionModal.PartitionModal_Tab_CommunicationStatus),
                iconName: IconName.DEVICE_INFO,
                onClick: (): void => setActiveTabID(PartitionConfigurationTabID.COMMUNICATION_STATUS)
            },
            content: (
                <PartitionCommunicationStatusTab
                    viewOnly={viewOnly}
                    telSysConfigSyncStartedAt={partitionCommStatusReadOnly.telSysConfigSyncStartedAt}
                    telSysConfigSyncLastError={partitionCommStatusReadOnly.telSysConfigSyncLastError}
                />
            ),
            hasError: validationErrors[PartitionConfigurationTabID.COMMUNICATION_STATUS]
        }
    ]

    if (isLoading)
        return <div className='partition-config-loading'>{language.get(PartitionModal.PartitionModal_Loading)}</div>

    return (
        <div className='partition-configuration-tabs'>
            <FormProvider {...methods}>
                <form
                    // eslint-disable-next-line @typescript-eslint/no-misused-promises
                    onSubmit={handleSubmit(submitForm)}
                >
                    <Tabs tabs={tabs} activeTabID={activeTabID} />
                    {viewOnly ? (
                        <div className='partition-config-footer modal-footer'>
                            <RoundedButton type='button' onClick={closeModal} shrink>
                                {language.get(PartitionModal.PartitionModal_Button_Close)}
                            </RoundedButton>
                        </div>
                    ) : (
                        <div className='partition-config-footer modal-footer'>
                            {isStringNotEmpty(serverError) ? <div>{serverError}</div> : <></>}
                            <RoundedButton type='button' onClick={closeModal} disabled={isSubmitting} shrink>
                                {language.get(PartitionModal.PartitionModal_Button_Cancel)}
                            </RoundedButton>
                            <RoundedButton type='submit' disabled={isSubmitting} shrink>
                                {language.get(PartitionModal.PartitionModal_Button_Save)}
                            </RoundedButton>
                        </div>
                    )}
                </form>
            </FormProvider>
        </div>
    )
}
